import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { navigateToIndexWithPrefill, navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useLazyGetInspectTimelineQuery,
  useLazyGetUrlActivityQuery,
} from '../api/indexerApi';
import type { UrlActivityEntry } from '../api/types';
import { urlActivityColumnConfig } from '../config/urlActivityColumnConfig';
import { HISTORY_WINDOW_LABEL } from '../lib/job-range';
import { decodeUrlForDisplay, formatFaNumber } from '../lib/utils';
import { INDEXER_AG_GRID_FILL_HEIGHT, IndexerAgGrid } from './IndexerAgGrid';
import { UrlInspectHistory } from './UrlInspectHistory';

export function UrlActivityModal({
  projectId,
  url,
  show,
  onHide,
  inspectCount,
}: {
  projectId: string;
  url: string;
  show: boolean;
  onHide: () => void;
  /** از لیست آدرس‌ها؛ اگر نباشد از timeline خوانده می‌شود. */
  inspectCount?: number;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<UrlActivityEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [resolvedInspectCount, setResolvedInspectCount] = useState<
    number | undefined
  >(inspectCount);
  const [getUrlActivity] = useLazyGetUrlActivityQuery();
  const [getInspectTimeline] = useLazyGetInspectTimelineQuery();

  const columns = useMemo(() => urlActivityColumnConfig(), []);
  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: UrlActivityEntry }) => params.data.id,
      rowHeight: 56,
      animateRows: false,
      suppressCellFocus: true,
    }),
    [],
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getUrlActivity({ projectId, url, page: 1, pageSize: 20 }, true)
      .unwrap()
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setPage(data.page);
        setTotal(data.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          getIndexerErrorInfo(err).message || 'خواندن تاریخچه ناموفق بود',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, projectId, url, getUrlActivity]);

  useEffect(() => {
    if (!show) return;
    if (inspectCount !== undefined) {
      setResolvedInspectCount(inspectCount);
      return;
    }
    let cancelled = false;
    setResolvedInspectCount(undefined);
    void getInspectTimeline({ projectId, url }, true)
      .unwrap()
      .then((data) => {
        if (!cancelled) setResolvedInspectCount(data.count);
      })
      .catch(() => {
        if (!cancelled) setResolvedInspectCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [show, projectId, url, inspectCount, getInspectTimeline]);

  async function loadMore() {
    const nextPage = page + 1;
    setLoading(true);
    try {
      const data = await getUrlActivity(
        { projectId, url, page: nextPage, pageSize: 20 },
        true,
      ).unwrap();
      setItems((prev) => [...prev, ...data.items]);
      setPage(data.page);
      setTotal(data.total);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message || 'بارگذاری بیشتر ناموفق بود',
      );
    } finally {
      setLoading(false);
    }
  }

  function requestIndex() {
    onHide();
    navigateToIndexWithPrefill(navigate, [url], 'url-list');
  }

  function reinspect() {
    onHide();
    navigateToInspectWithPrefill(navigate, [url], 'url-list');
  }

  const hasMore = items.length < total;
  const decoded = decodeUrlForDisplay(url);
  const showInspectDetails =
    resolvedInspectCount === undefined || resolvedInspectCount > 0;

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        dialogClassName="indexer-grid-modal"
      >
        <Modal.Header closeButton className="bg-body-tertiary border-bottom">
          <Modal.Title as="div" className="fs--1 fw-semibold mb-0 lh-sm">
            <div>تاریخچه فعالیت</div>
            <div className="text-secondary fs--2 fw-normal mt-1">
              {HISTORY_WINDOW_LABEL}
              {total > 0 ? ` · ${formatFaNumber(total)} رویداد` : null}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <div
            className="d-flex align-items-center gap-2 border rounded-3 px-3 py-2 mb-3 bg-body flex-shrink-0"
            title={decoded}
            dir="ltr"
          >
            <span className="mono text-break text-primary fs--1">{decoded}</span>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3 flex-shrink-0">
            <Button size="sm" variant="outline-secondary" onClick={requestIndex}>
              درخواست ایندکس
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={reinspect}>
              بازرسی وضعیت
            </Button>
            {showInspectDetails && (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setInspectOpen(true)}
              >
                {resolvedInspectCount !== undefined
                  ? `جزئیات بازرسی (${formatFaNumber(resolvedInspectCount)})`
                  : 'جزئیات بازرسی'}
              </Button>
            )}
          </div>

          {loading && items.length === 0 && (
            <div className="text-center py-5 text-secondary">
              <Spinner
                size="sm"
                animation="border"
                className="me-2"
                style={{ width: 12, height: 12 }}
              />
              در حال بارگذاری…
            </div>
          )}
          {error && <div className="text-danger fs--1 mb-3">{error}</div>}

          {items.length === 0 && !loading && (
            <div className="text-secondary text-center py-5 border border-dashed rounded-3">
              در {HISTORY_WINDOW_LABEL} فعالیتی برای این آدرس ثبت نشده است.
            </div>
          )}

          {items.length > 0 && (
            <div className="indexer-fill-grid url-activity-grid border rounded-3 overflow-hidden ag-grid-in-card">
              <div className="indexer-fill-grid-body">
                <IndexerAgGrid
                  tableName="indexer_url_activity"
                  columns={columns}
                  data={items}
                  height={INDEXER_AG_GRID_FILL_HEIGHT}
                  otherOptions={otherOptions}
                />
              </div>
            </div>
          )}

          {hasMore && (
            <div className="text-center mt-3 flex-shrink-0">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={loading}
                onClick={() => void loadMore()}
              >
                {loading
                  ? 'در حال بارگذاری…'
                  : `بارگذاری بیشتر (${formatFaNumber(items.length)} از ${formatFaNumber(total)})`}
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {inspectOpen && (
        <UrlInspectHistory
          projectId={projectId}
          url={url}
          show
          onHide={() => setInspectOpen(false)}
        />
      )}
    </>
  );
}
